import { uploadFileDirect, createContentDocumentDirect } from './uploadService';
import { ContentRating } from '../constants/contentRatings';
import { Genre } from '../constants/genres';

export interface SubtitleFile {
  file: File;
  language: string;
  id: string;
}

export interface UploadFormData {
  title: string;
  description: string;
  category: string;
  tags: string;
  visibility: string;
  rating: ContentRating | '';
  language: string;
  genres: Genre[];
  subtitles: File[];
  trailer: File | null;
  poster: File | null;
  posterFile: File | null;
  posterUrl: string;
  trailerFile: File | null;
  trailerUrl: string;
  subtitleFiles: SubtitleFile[];
  thumbnail: File | null;
  audioTracks: File[];
  ageRestriction: boolean;
  donationRequired: boolean;
  commentsEnabled: boolean;
  linkedContent: string;
  seasonNumber: string;
  episodeNumber: string;
  chapterNumber: string;
  cast: string;
  director: string;
  writer: string;
  contentAdvisory: string;
  languages: string[];
  seriesPoster: File | null;
  seriesTrailer: File | null;
  episodes: Array<{
    id: string;
    name: string;
    video: File | null;
    subtitles: File[];
  }>;
}

export interface UploadState {
  uploadProgress: number;
  isUploading: boolean;
  uploadedFiles: File[];
  uploadedFileUrls: string[];
  uploadingFiles: Set<string>;
  errors: Record<string, string>;
}

export class UploadBusinessLogic {
  static validateForm(formData: UploadFormData, uploadedFiles: File[], uploadedFileUrls: string[]): Record<string, string> {
    const errors: Record<string, string> = {};
    
    if (!formData.title.trim()) errors.title = 'Title is required';
    if (!formData.description.trim()) errors.description = 'Description is required';
    if (!formData.category) errors.category = 'Category is required';
    
    // For series, check if episodes have videos; for other content, check uploadedFiles
    if (formData.category === 'series') {
      if (!formData.episodes || formData.episodes.length === 0) {
        errors.episodes = 'At least one episode is required';
      } else {
        const episodesWithVideos = formData.episodes.filter(ep => ep.video);
        if (episodesWithVideos.length === 0) {
          errors.episodes = 'At least one episode must have a video';
        }
      }
    } else {
      if (uploadedFiles.length === 0) errors.files = 'At least one file is required';
      // Check if all files are uploaded
      if (uploadedFiles.length > 0 && uploadedFileUrls.length !== uploadedFiles.length) {
        errors.files = 'Please wait for all files to finish uploading';
      }
    }

    return errors;
  }

  static validateFile(file: File, type: 'image' | 'video' | 'subtitle'): string | null {
    const maxSizes = {
      image: 10 * 1024 * 1024, // 10MB
      video: 2 * 1024 * 1024 * 1024, // 2GB (reduced from 32GB for security)
      subtitle: 5 * 1024 * 1024 // 5MB
    };

    if (file.size > maxSizes[type]) {
      return `File size too large. Maximum size for ${type} files: ${(maxSizes[type] / 1024 / 1024).toFixed(0)}MB`;
    }

    const allowedTypes = {
      image: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
      video: ['video/mp4', 'video/avi', 'video/mov', 'video/wmv'],
      subtitle: ['text/plain', 'application/x-subrip', 'text/vtt', 'application/octet-stream', '']
    };

    // For subtitle files, also check the file extension as browsers often misidentify MIME types
    if (type === 'subtitle') {
      const fileName = file.name.toLowerCase();
      const hasValidExtension = fileName.endsWith('.srt') || fileName.endsWith('.vtt') || fileName.endsWith('.ass') || fileName.endsWith('.txt');
      
      if (!allowedTypes[type].includes(file.type) && !hasValidExtension) {
        return `Invalid file type. Allowed types: SRT, VTT, ASS, or TXT files`;
      }
    } else {
      if (!allowedTypes[type].includes(file.type)) {
        return `Invalid file type. Allowed types: ${allowedTypes[type].join(', ')}`;
      }
    }

    return null;
  }

  static async handleFileUpload(
    files: File[],
    userId: string,
    onProgress: (progress: number) => void,
    onFileUploaded: (url: string) => void,
    onError: (error: string) => void
  ): Promise<void> {
    for (const file of files) {
      const fileId = `${file.name}-${file.size}`;
      
      try {
        const url = await uploadFileDirect(`users/${userId}/assets`, file);
        onFileUploaded(url);
      } catch (error) {
        onError(`Failed to upload ${file.name}`);
      }
    }
  }

  static async handleSeriesUpload(
    formData: UploadFormData,
    userId: string,
    onProgress: (progress: number) => void
  ): Promise<string> {
    const assetUrls: Record<string, string | string[]> = {};
    let totalFiles = 0;
    let uploadedCount = 0;

    // Upload series poster and trailer if provided
    if (formData.seriesPoster) {
      totalFiles++;
      const seriesPosterUrl = await uploadFileDirect(`users/${userId}/series/${Date.now()}`, formData.seriesPoster);
      assetUrls.poster = seriesPosterUrl;
      uploadedCount++;
      onProgress((uploadedCount / totalFiles) * 100);
    }
    
    if (formData.seriesTrailer) {
      totalFiles++;
      const seriesTrailerUrl = await uploadFileDirect(`users/${userId}/series/${Date.now()}`, formData.seriesTrailer);
      assetUrls.trailer = seriesTrailerUrl;
      uploadedCount++;
      onProgress((uploadedCount / totalFiles) * 100);
    }

    const payload = {
      title: formData.title,
      description: formData.description,
      category: formData.category,
      visibility: formData.visibility,
      rating: formData.rating,
      language: formData.language,
      genres: formData.genres,
      linkedContent: formData.linkedContent,
      seasonNumber: formData.seasonNumber,
      episodeNumber: formData.episodeNumber,
      chapterNumber: formData.chapterNumber,
      cast: formData.cast.split(',').map(name => name.trim()),
      director: formData.director.split(',').map(name => name.trim()),
      writer: formData.writer.split(',').map(name => name.trim()),
      contentAdvisory: formData.contentAdvisory.split(',').map(advisory => advisory.trim()),
    };

    const seriesId = await createContentDocumentDirect(userId, payload, assetUrls);
    
    // Upload each episode
    for (let i = 0; i < formData.episodes.length; i++) {
      const episode = formData.episodes[i];
      
      if (episode.video) {
        try {
          const episodeVideoUrl = await uploadFileDirect(`users/${userId}/episodes/${seriesId}`, episode.video);
          
          const episodeAssetUrls: { [key: string]: string } = {
            video: episodeVideoUrl
          };
          
          if (episode.subtitles && episode.subtitles.length > 0) {
            const subtitleUrls: string[] = [];
            for (const subtitle of episode.subtitles) {
              const subtitleUrl = await uploadFileDirect(`users/${userId}/episodes/${seriesId}/subtitles`, subtitle);
              subtitleUrls.push(subtitleUrl);
            }
            episodeAssetUrls.subtitles = subtitleUrls.join(',');
          }
          
          await createContentDocumentDirect(userId, {
            title: episode.name,
            description: '',
            category: 'episode',
            visibility: formData.visibility,
            rating: formData.rating,
            language: formData.language,
            genres: formData.genres,
            tags: formData.tags,
            linkedContent: seriesId,
            seasonNumber: formData.seasonNumber,
            episodeNumber: (i + 1).toString(),
            chapterNumber: ''
          }, episodeAssetUrls);
        } catch (error) {
          console.error(`Error uploading episode ${i + 1}:`, error);
        }
      }
    }
    
    return seriesId;
  }

  static async handleSingleContentUpload(
    formData: UploadFormData,
    userId: string,
    uploadedFiles: File[],
    uploadedFileUrls: string[],
    onProgress: (progress: number) => void
  ): Promise<string> {
    const assetUrls: Record<string, string | string[]> = {};
    let totalFiles = uploadedFiles.length + (formData.posterFile ? 1 : 0) + (formData.trailerFile ? 1 : 0) + (formData.subtitleFiles ? formData.subtitleFiles.length : 0);
    let uploadedCount = 0;

    // Upload main content files
    for (let i = 0; i < uploadedFiles.length; i++) {
      const file = uploadedFiles[i];
      const url = uploadedFileUrls[i];
      if (url) {
        // File already uploaded, use existing URL
        assetUrls[file.name] = url;
        uploadedCount++;
        onProgress((uploadedCount / totalFiles) * 100);
      } else {
        // File not uploaded yet, upload it now
        const fileUrl = await uploadFileDirect(`users/${userId}/content`, file);
        assetUrls[file.name] = fileUrl;
        uploadedCount++;
        onProgress((uploadedCount / totalFiles) * 100);
      }
    }

    // Upload poster/thumbnail
    if (formData.posterFile) {
      const posterUrl = await uploadFileDirect(`users/${userId}/poster`, formData.posterFile);
      assetUrls['poster'] = posterUrl;
      uploadedCount++;
      onProgress((uploadedCount / totalFiles) * 100);
    }

    // Upload trailer
    if (formData.trailerFile) {
      const trailerUrl = await uploadFileDirect(`users/${userId}/trailer`, formData.trailerFile);
      assetUrls['trailer'] = trailerUrl;
      uploadedCount++;
      onProgress((uploadedCount / totalFiles) * 100);
    }

    // Upload subtitles
    if (formData.subtitleFiles && formData.subtitleFiles.length > 0) {
      const subtitleUrls: string[] = [];
      for (const subtitle of formData.subtitleFiles) {
        const subtitleUrl = await uploadFileDirect(`users/${userId}/subtitles`, subtitle.file);
        subtitleUrls.push(subtitleUrl);
        uploadedCount++;
        onProgress((uploadedCount / totalFiles) * 100);
      }
      assetUrls['subtitles'] = subtitleUrls;
    }

    // Import sanitization utilities
    const { sanitizeContentMetadata, sanitizeText, sanitizeStringArray } = await import('../utils/inputSanitizer');
    
    // Sanitize all user inputs before creating payload
    const sanitizedData = sanitizeContentMetadata({
      title: formData.title,
      description: formData.description,
      tags: formData.tags,
      cast: formData.cast,
      director: formData.director,
      writer: formData.writer,
      contentAdvisory: formData.contentAdvisory,
    });

    const payload = {
      title: sanitizedData.title,
      description: sanitizedData.description,
      category: formData.category, // Category is from dropdown, safe
      visibility: formData.visibility, // Visibility is from dropdown, safe
      rating: formData.rating, // Rating is from dropdown, safe
      language: sanitizeText(formData.language || 'English', 50),
      languages: Array.isArray(formData.languages) 
        ? formData.languages.map(lang => sanitizeText(lang, 50))
        : [],
      genres: Array.isArray(formData.genres) ? formData.genres : [],
      tags: sanitizedData.tags,
      linkedContent: formData.linkedContent ? sanitizeText(formData.linkedContent, 100) : '',
      seasonNumber: sanitizeText(formData.seasonNumber || '', 10),
      episodeNumber: sanitizeText(formData.episodeNumber || '', 10),
      chapterNumber: sanitizeText(formData.chapterNumber || '', 10),
      cast: sanitizedData.cast,
      director: sanitizedData.director,
      writer: sanitizedData.writer,
      contentAdvisory: sanitizedData.contentAdvisory,
      commentsEnabled: formData.commentsEnabled !== false, // Default to true if not set
      ageRestriction: formData.ageRestriction === true,
      donationRequired: formData.donationRequired === true,
    };

    return await createContentDocumentDirect(userId, payload, assetUrls);
  }
}
